import { client } from '~/generated/api/client.gen'
import { apiErrorDetails, isUnansweredWrite } from '~/utils/apiError'

/**
 * Names the frontend build on every API request, so backend error logs can
 * tell a stale cached client from the current one.
 */
export const CLIENT_VERSION_HEADER = 'Advisor-Client-Version'

export default defineNuxtPlugin((nuxtApp) => {
  const appConfig = useAppConfig()
  const baseUrl = appConfig.DEV_ENDPOINT
  if (!baseUrl)
    throw new Error('Missing DEV_ENDPOINT app configuration')

  client.setConfig({
    baseUrl,
    auth: async () => {
      const response = await (
        nuxtApp.$acquireTokenSilent as () => Promise<{
          accessToken: string
        } | null>
      )()

      return response?.accessToken
    },
  })

  client.interceptors.request.use((request, options) => {
    if (options?.security?.length && !request.headers.has('Authorization')) {
      const error = new Error('Authentication required')
      Object.assign(error, { statusCode: 401, code: 'auth_missing', localAuthMissing: true })
      throw error
    }
    if (appConfig.buildDate)
      request.headers.set(CLIENT_VERSION_HEADER, appConfig.buildDate)
    const pathname = new URL(request.url).pathname
    if (!pathname.startsWith('/v1/')) {
      console.warn('[deprecated-api-endpoint]', request.method, pathname)
    }
    return request
  })

  client.interceptors.error.use((error, response, request) => {
    if (isUnansweredWrite(error, response, request)) {
      // A firewall block or a connection failure can hide the response. The
      // write may already have succeeded, so its outcome remains unknown.
      return Object.assign(error, { code: 'write_unanswered' })
    }
    if (error instanceof Error) {
      Object.assign(error, {
        statusCode: response?.status ?? (error as { statusCode?: number }).statusCode,
      })
      return error
    }

    // The message names the error code, never the body's message: it reaches
    // Sentry, and public API messages are generic or could carry user data.
    const details = apiErrorDetails(error, response?.status)
    return Object.assign(
      new Error(details.code ?? `HTTP ${String(details.statusCode ?? 'error')}`),
      {
        data: error,
        statusCode: details.statusCode,
        code: details.code,
        exceptionId: details.exceptionId,
      },
    )
  })
})
